import { useState } from 'react';
import Button from '@johnlewispartnership/wtr-ingredients/dist/Button';
import Typography from '@johnlewispartnership/wtr-ingredients/dist/foundations/typography';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import { LabeledControl } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/LabeledControl';
import { Radio } from '@johnlewispartnership/wtr-ingredients/ingredients/Radio';
import { useCookieConsent } from 'renderer/components/CookieConsent/context';
import urls from 'constants/urls';

import styles from '../CookieConsent.module.scss';

export const CookiePreferences = ({ updatePreferences }) => {
  const { functionalCookiesAccepted, analyticsCookiesAccepted, advertisingCookiesAccepted } =
    useCookieConsent();
  const [functional, setFunctional] = useState(functionalCookiesAccepted);
  const [analytics, setAnalytics] = useState(analyticsCookiesAccepted);
  const [advertising, setAdvertising] = useState(advertisingCookiesAccepted);

  const handleClose = e => {
    e.preventDefault();
    updatePreferences({
      advertising,
      analytics,
      functional,
    });
  };

  return (
    <form onSubmit={handleClose} name="marketing-preferences">
      <section className={styles.modal} data-testid="waitrose-cookie-preferences-banner">
        <div className={styles.optionsContainer}>
          <Typography>
            Our website uses cookies to improve your experience and personalise content. Cookies are
            small files placed on your computer or mobile device when you visit a website. They are
            widely used to improve your experience of a website, gather reporting information and
            show relevant advertising. You can allow all cookies or manage them for yourself. You
            can find out more on our{' '}
            <TextLink href={urls.waitroseCookies} className={styles.waitroseCookiesLink}>
              cookies page
            </TextLink>{' '}
            any time.
          </Typography>
          <Typography element="h3">Essential Cookies</Typography>
          <Typography className={styles.cookieOptionDescription}>
            These cookies are needed for essential functions such as signing in, storing products in
            your trolley and making payments. They can&apos;t be switched off.
          </Typography>
          <div className={styles.radioContainer}>
            <LabeledControl
              id="essential-cookies-confirm"
              control={<Radio name="essentialCookies" />}
              label="Allow"
              boxed
              readOnly
              checked
              disabled
            />
          </div>
          <Typography element="h3">Functional Cookies</Typography>
          <Typography className={styles.cookieOptionDescription}>
            By accepting functional cookies, you will be able to see certain features on our site
            that aren&apos;t essential to its running but still add significant value to customers,
            such as product reviews. If you choose not to accept these cookies, you can update your
            preferences at any time.
          </Typography>
          <div className={styles.radioContainer}>
            <LabeledControl
              id="functional-cookies-confirm"
              control={<Radio name="functionalCookies" />}
              label="Allow"
              value="true"
              checked={functional}
              onChange={() => setFunctional(true)}
              boxed
            />
            <LabeledControl
              id="functional-cookies-deny"
              control={<Radio name="functionalCookies" />}
              label="Don't allow"
              value="false"
              checked={!functional}
              onChange={() => setFunctional(false)}
              boxed
            />
          </div>
          <Typography element="h3">Analytical Cookies</Typography>
          <Typography className={styles.cookieOptionDescription}>
            These cookies help us optimise our website based on data. Using these cookies we will
            know which web pages customers enjoy reading most and what products are most popular.
          </Typography>
          <div className={styles.radioContainer}>
            <LabeledControl
              id="analytics-cookies-confirm"
              control={<Radio name="analyticsCookies" />}
              label="Allow"
              value="true"
              checked={analytics}
              onChange={() => setAnalytics(true)}
              boxed
            />
            <LabeledControl
              id="analytics-cookies-deny"
              control={<Radio name="analyticsCookies" />}
              label="Don't allow"
              value="false"
              checked={!analytics}
              onChange={() => setAnalytics(false)}
              boxed
            />
          </div>
          <Typography element="h3">Advertising Cookies</Typography>
          <Typography className={styles.cookieOptionDescription}>
            These cookies are used to help prevent you from seeing irrelevant advertising. They are
            used to advertise Waitrose & Partners items when you visit other websites based on what
            you&apos;ve browsed, searched for or bought.
          </Typography>
          <div className={styles.radioContainer}>
            <LabeledControl
              id="advertising-cookies-confirm"
              control={<Radio name="advertisingCookies" />}
              label="Allow"
              value="true"
              checked={advertising}
              onChange={() => setAdvertising(true)}
              boxed
            />
            <LabeledControl
              id="advertising-cookies-deny"
              control={<Radio name="advertisingCookies" />}
              label="Don't allow"
              value="false"
              checked={!advertising}
              onChange={() => setAdvertising(false)}
              boxed
            />
          </div>
        </div>
        <section className={styles.acceptCookieCTA}>
          <Button data-testid="submit" type="submit" width="standard">
            Accept selected
          </Button>
        </section>
      </section>
    </form>
  );
};
